import { useEffect, useMemo, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import Badge from "react-bootstrap/Badge";
import Button from "react-bootstrap/Button";
import isEqual from "lodash/isEqual";
import cloneDeep from "lodash/cloneDeep";
import { VERIFICATION_SERVICES_URL } from "@constants/routes";
import { ModalTypes } from "@enums/modal-types";
import { ChangeRequestStatusTypes } from "@enums/change-request-status-types";
import { FormattedRpServiceConfiguration } from "@idw-types/data/rp-service-configuration";
import {
    AxnRelyingParty,
    AxnAttributeTypeField,
    SimpleAxnWorkflow,
    AxnAvailablePropertyCategory,
    AxnAttributeType,
    AxnCompany,
    AxnAttributeTypeFieldCustomization
} from "@idw-types/data/reference-data/axn";
import { LocaleTypes } from "@iddataweb/idw-common-ui/enums";
import { resources } from "@constants/hardcoded-strings";
import { EMAIL_TEMPLATE_FIELDS_PROPERTY_TYPE_ID } from "@constants/verification-service-details-constants";
import { ButtonTypes } from "@enums/button-types";
import { useDiscardChangesModal } from "@hooks/use-discard-changes-modal";
import {
    AssertionAttribute,
    AttributeLabel,
    AvailableAssertionStatus,
    RpServiceAttributeProperty,
    ScoreAttributeMap,
    ServiceOfferingRefreshRate
} from "@idw-types/data/reference-data/generic";
import { IDWSession } from "@idw-types/data/idw-session";
import { ScoreReferenceData } from "@idw-types/data/rp-service-configuration/scores/score-reference-data";
import { FormattedMessagingTemplate, MessagingTemplateConfiguration, MessagingTemplateCache } from "@idw-types/data/messaging-template";
import { EmailTemplateFields } from "@idw-types/common/email-template-fields";
import { AxnDocumentationLink } from "@idw-types/data/reference-data/axn/axn-documentation-link";
import { AxnAcquiredAttribute } from "@idw-types/data/reference-data/axn/axn-acquired-attribute";
import { RpServiceChangeRequest } from "@idw-types/data/rp-service-change-request";
import { VerificationServiceDetailsValidationErrors } from "@idw-types/common/validation/verification-service-details-validation-errors";
import {
    getEmailTemplateFieldsAndMessagingTemplateCacheUpdates,
    getCleanedEmailTemplateFields,
    getAvailableMessagingTemplateTypesAndEmailTemplateFieldsFromRpServiceConfig
} from "@utils/messaging-template/messaging-template-util";
import { navigateToUtil } from "@utils/navigation-util";
import { getSelectedMessagingTemplates } from "@utils/messaging-template/messaging-template-util";
import { ButtonWithSpinner } from "@containers/common/button-with-spinner/button-with-spinner";
import { VerificationServiceDetailsDropdownMenu } from "@containers/verification-service-details/verification-service-details-tab-container/verification-service-details-dropdown-menu/verification-service-details-dropdown-menu";
import { VerificationServiceDetailsTabContainer } from "@containers/verification-service-details/verification-service-details-tab-container/verification-service-details-tab-container";
import styles from "./verification-service-details-component.less";
import { AxnRpService } from "@idw-types/data/axn-rp-service";
import { SimpleRPServiceWorkflow } from "@idw-types/data/reference-data/axn/simple-rp-service-workflow";
import { ApAssertionStatusMap } from "@idw-types/data/ap-assertion-status-map/ap-assertion-status-map";

type VerificationServiceDetailsComponentProps = {
    selectedLanguage: LocaleTypes;
    availableLanguages: Array<LocaleTypes>;
    setSelectedLanguage: (newLanguage: LocaleTypes) => void;
    setAvailableLanguages: (availableLanguages: Array<LocaleTypes>) => void;
    session: IDWSession;
    attributeLabels: Array<AttributeLabel>;
    assertionAttributes: Array<AssertionAttribute>;
    assertionStatusMaps: Array<ApAssertionStatusMap>;
    assertionScoreServiceOfferingIDs: Array<string>;
    trustScoreServiceOfferingIDs: Array<string>;
    scoreReferenceData: ScoreReferenceData;
    scoreAttributeMaps: Array<ScoreAttributeMap>;
    axnRpServices: Array<AxnRpService>;
    axnRelyingParties: Array<AxnRelyingParty>;
    axnAttributeTypes: Array<AxnAttributeType>;
    axnAttributeTypeFields: Array<AxnAttributeTypeField>;
    serviceOfferingRefreshRates: Array<ServiceOfferingRefreshRate>;
    serviceOfferingRefreshRateDefault: string;
    serviceAttributeProperties: Array<RpServiceAttributeProperty>;
    initialRpServiceConfiguration: FormattedRpServiceConfiguration;
    simpleAXNAdminRPServiceWorkflows: Array<SimpleRPServiceWorkflow>;
    simpleAXNRPServiceWorkflows: Array<SimpleRPServiceWorkflow>;
    axnAvailableAssertionStatuses: Array<AvailableAssertionStatus>;
    handleSave: (rpServiceConfiguration: FormattedRpServiceConfiguration) => Promise<FormattedRpServiceConfiguration>;
    simpleAxnWorkflows: Array<SimpleAxnWorkflow>;
    simpleAxnAdminWorkflows: Array<SimpleAxnWorkflow>;
    availablePropertyCategories: Array<AxnAvailablePropertyCategory>;
    axnCompanies: Array<AxnCompany>;
    axnDocumentationLinks: Array<AxnDocumentationLink>;
    axnAcquiredAttributes: Array<AxnAcquiredAttribute>;
    rpServiceChangeRequests: Array<RpServiceChangeRequest>;
    messagingTemplateConfigurations: Array<MessagingTemplateConfiguration>;
    axnAttributeTypeFieldCustomizations: Array<AxnAttributeTypeFieldCustomization>;
    setRpServiceChangeRequest: (changeRequest: RpServiceChangeRequest) => void;
};

export const VerificationServiceDetailsComponent = (props: VerificationServiceDetailsComponentProps) => {
    const {
        selectedLanguage,
        availableLanguages,
        setSelectedLanguage,
        setAvailableLanguages,
        session,
        attributeLabels,
        assertionAttributes,
        assertionStatusMaps,
        assertionScoreServiceOfferingIDs,
        trustScoreServiceOfferingIDs,
        axnRpServices,
        axnRelyingParties,
        axnAttributeTypes,
        axnAttributeTypeFields,
        serviceOfferingRefreshRates,
        serviceOfferingRefreshRateDefault,
        initialRpServiceConfiguration,
        scoreReferenceData,
        scoreAttributeMaps,
        serviceAttributeProperties,
        simpleAXNAdminRPServiceWorkflows,
        simpleAXNRPServiceWorkflows,
        simpleAxnWorkflows,
        simpleAxnAdminWorkflows,
        handleSave,
        axnCompanies,
        axnDocumentationLinks,
        availablePropertyCategories,
        axnAcquiredAttributes,
        rpServiceChangeRequests,
        axnAvailableAssertionStatuses,
        axnAttributeTypeFieldCustomizations,
        setRpServiceChangeRequest,
        messagingTemplateConfigurations
    } = props;

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const routerLocation = useLocation();
    const [requestInProgress, setRequestInProgress] = useState(false);
    const [hasFetchedMessagingTemplates, setHasFetchedMessagingTemplates] = useState(false);
    const [rpServiceConfigurationState, setRpServiceConfigurationState] = useState<FormattedRpServiceConfiguration>(cloneDeep(initialRpServiceConfiguration));
    const [messagingTemplateCache, setMessagingTemplateCache] = useState<MessagingTemplateCache>({});

    const initialSelectedMessagingTemplates = useMemo(() => {
        if (hasFetchedMessagingTemplates) {
            const {
                availableMessagingTemplateTypes: initialAMTT,
                mergedEmailTemplateFields: initialMETF
            } = getAvailableMessagingTemplateTypesAndEmailTemplateFieldsFromRpServiceConfig(initialRpServiceConfiguration);
            
            return getSelectedMessagingTemplates(initialAMTT, initialMETF, messagingTemplateCache);
        }
        return [];
    }, [initialRpServiceConfiguration, hasFetchedMessagingTemplates]);

    const {
        availableMessagingTemplateTypes,
        mergedEmailTemplateFields,
        inheritedEmailTemplateFields
    } = useMemo(() => {
        return getAvailableMessagingTemplateTypesAndEmailTemplateFieldsFromRpServiceConfig(rpServiceConfigurationState);
    }, [rpServiceConfigurationState]);

    const selectedMessagingTemplates = useMemo(() => {
        return getSelectedMessagingTemplates(availableMessagingTemplateTypes, mergedEmailTemplateFields, messagingTemplateCache);
    }, [availableMessagingTemplateTypes, mergedEmailTemplateFields, messagingTemplateCache]);

    const stateIsUnchanged = useMemo(() => {
        const isRpServiceEqual = isEqual(initialRpServiceConfiguration, rpServiceConfigurationState);
        if (hasFetchedMessagingTemplates) {
            return isRpServiceEqual && isEqual(initialSelectedMessagingTemplates, selectedMessagingTemplates);
        }

        return isRpServiceEqual;
    }, [
        initialRpServiceConfiguration,
        rpServiceConfigurationState,
        initialSelectedMessagingTemplates,
        selectedMessagingTemplates,
        hasFetchedMessagingTemplates
    ]);

    useDiscardChangesModal({
        modalIdPrefix: "discard-verification-service-changes",
        modalType: ModalTypes.DiscardVerificationServiceChanges,
        stateIsUnchanged
    });

    useEffect(() => {
        setRpServiceConfigurationState(cloneDeep(initialRpServiceConfiguration));
    }, [initialRpServiceConfiguration]);

    const handleSaveWithMessagingTemplatesUpdates = async (
        selectedMessagingTemplates: Array<FormattedMessagingTemplate>,
        mergedEmailTemplateFields: EmailTemplateFields
    ) => {
        const {
            newEmailTemplateFields,
            newMessagingTemplateCache
        } = await getEmailTemplateFieldsAndMessagingTemplateCacheUpdates(dispatch)(
            selectedMessagingTemplates,
            axnCompanies,
            axnRelyingParties
        );

        //update the messaging template cache
        setMessagingTemplateCache({
            ...messagingTemplateCache,
            ...newMessagingTemplateCache
        });

        //clean out all old data stored for template/language files, email subjects and headerlogo
        //new data will be populated from the selectedMessagingTemplate list
        //only leaves the pinEmailFromAddress and dcuiEmailFromAddress
        const cleanedEmailTemplateFields = getCleanedEmailTemplateFields(mergedEmailTemplateFields);

        //merge the updated emailTemplateFields into the property
        const newProperties = rpServiceConfigurationState.properties.map(property => {
            if (property.propertyTypeId === EMAIL_TEMPLATE_FIELDS_PROPERTY_TYPE_ID) {
                //merge the new email template fields generated for the selected messaging templates
                //with the cleaned data
                return {
                    ...property,
                    value: JSON.stringify({
                        ...cleanedEmailTemplateFields,
                        ...newEmailTemplateFields
                    })
                };
            }
            return property;
        });

        //save all of the changes + the new template uuids in the properties
        await handleSave({ ...rpServiceConfigurationState, properties: newProperties });
    };

    const onClickClose = () => {
        navigateToUtil(VERIFICATION_SERVICES_URL, navigate, routerLocation.pathname);
    };

    const onClickSave = async (
        selectedMessagingTemplates: Array<FormattedMessagingTemplate>,
        mergedEmailTemplateFields: EmailTemplateFields
    ) => {
        setRequestInProgress(true);

        //if any of the selectedMessagingTemplates don't exist in the database yet
        //we need to create them, add the new templates to the cache, and update the
        //emailTemplateFields property to set the fields to the new UUIDs
        if (!isEqual(initialSelectedMessagingTemplates, selectedMessagingTemplates)) {
            await handleSaveWithMessagingTemplatesUpdates(selectedMessagingTemplates, mergedEmailTemplateFields);
        } else {
            await handleSave(rpServiceConfigurationState);
        }

        setRequestInProgress(false);
    };

    const handleSetHasFetchedMessagingTemplates = () => {
        setHasFetchedMessagingTemplates(true);
    };

    const hasUnsubmittedChangeRequest = useMemo(() => {
        if (rpServiceChangeRequests.length > 0) {
            return rpServiceChangeRequests[0].changeRequestStatus === ChangeRequestStatusTypes.UNSUBMITTED;
        }
        return false;
    }, [rpServiceChangeRequests]);

    const simpleRPServiceWorkflows = useMemo(() => {
        return hasUnsubmittedChangeRequest ? simpleAXNAdminRPServiceWorkflows : simpleAXNRPServiceWorkflows;
    }, [hasUnsubmittedChangeRequest, simpleAXNRPServiceWorkflows, simpleAXNAdminRPServiceWorkflows]);


    const isUIDisabled = useMemo(() => {
        return !initialRpServiceConfiguration.permissions.canModify || !hasUnsubmittedChangeRequest || requestInProgress;
    }, [initialRpServiceConfiguration.permissions.canModify, hasUnsubmittedChangeRequest, requestInProgress]);

    const generateHeaderComponent = useCallback((
        selectedMessagingTemplates: Array<FormattedMessagingTemplate>,
        mergedEmailTemplateFields: EmailTemplateFields,
        validationErrors: VerificationServiceDetailsValidationErrors
    ) => {
        const handleSave = () => {
            onClickSave(selectedMessagingTemplates, mergedEmailTemplateFields);
        };

        return (
            <div className={styles.headerContainer}>
                <div className={styles.headerRow}>
                    <span className={styles.serviceName}>{rpServiceConfigurationState.serviceName}</span>
                    {rpServiceChangeRequests.length > 0 && (
                        <Badge pill bg={"secondary"}>{rpServiceChangeRequests[0].changeRequestStatus}</Badge>
                    )}
                    <div className={styles.buttonContainer}>
                        <Button id="verification-service-detail-close" variant={"outline-secondary"} onClick={onClickClose}>{resources.common.buttons.close.label}</Button>
                        {hasUnsubmittedChangeRequest && initialRpServiceConfiguration.permissions.canModify && (
                            <ButtonWithSpinner
                                id="verification-service-detail-save"
                                disabled={stateIsUnchanged || requestInProgress || validationErrors.count > 0}
                                hideSpinner={stateIsUnchanged || validationErrors.count > 0}
                                label={resources.common.buttons.save.label}
                                type={ButtonTypes.SUBMIT}
                                onClick={handleSave}
                                variant={"primary"}
                            />
                        )}
                        <VerificationServiceDetailsDropdownMenu
                            stateIsUnchanged={stateIsUnchanged}
                            validationErrors={validationErrors}
                            rpServiceConfiguration={initialRpServiceConfiguration}
                            rpServiceChangeRequests={rpServiceChangeRequests}
                            requestInProgress={requestInProgress}
                            setRpServiceConfiguration={setRpServiceConfigurationState}
                            setRpServiceChangeRequest={setRpServiceChangeRequest}
                            setRequestInProgress={setRequestInProgress}
                        />
                    </div>
                </div>
            </div>
        );
    }, [
        selectedMessagingTemplates,
        initialSelectedMessagingTemplates,
        messagingTemplateCache,
        rpServiceConfigurationState,
        rpServiceChangeRequests,
        hasUnsubmittedChangeRequest,
        initialRpServiceConfiguration,
        stateIsUnchanged,
        requestInProgress,
        setRpServiceConfigurationState,
        setRpServiceChangeRequest,
        setRequestInProgress
    ]);

    return (
        <div className={styles.verificationServiceDetailsComponent}>
            <VerificationServiceDetailsTabContainer
                assertionAttributes={assertionAttributes}
                assertionScoreServiceOfferingIDs={assertionScoreServiceOfferingIDs}
                assertionStatusMaps={assertionStatusMaps}
                attributeLabels={attributeLabels}
                availableLanguages={availableLanguages}
                availablePropertyCategories={availablePropertyCategories}
                axnAcquiredAttributes={axnAcquiredAttributes}
                axnAttributeTypeFields={axnAttributeTypeFields}
                axnAttributeTypes={axnAttributeTypes}
                axnCompanies={axnCompanies}
                axnDocumentationLinks={axnDocumentationLinks}
                axnRelyingParties={axnRelyingParties}
                axnRpServices={axnRpServices}
                initialRpServiceConfiguration={initialRpServiceConfiguration}
                isDisabled={isUIDisabled}
                requestInProgress={requestInProgress}
                rpServiceConfigurationState={rpServiceConfigurationState}
                scoreReferenceData={scoreReferenceData}
                scoreAttributeMaps={scoreAttributeMaps}
                selectedLanguage={selectedLanguage}
                serviceAttributeProperties={serviceAttributeProperties}
                serviceOfferingRefreshRateDefault={serviceOfferingRefreshRateDefault}
                serviceOfferingRefreshRates={serviceOfferingRefreshRates}
                session={session}
                generateHeaderComponent={generateHeaderComponent}
                setAvailableLanguages={setAvailableLanguages}
                setRpServiceConfigurationState={setRpServiceConfigurationState}
                setSelectedLanguage={setSelectedLanguage}
                simpleRPServiceWorkflows={simpleRPServiceWorkflows}
                simpleAxnAdminWorkflows={simpleAxnAdminWorkflows}
                simpleAxnWorkflows={simpleAxnWorkflows}
                trustScoreServiceOfferingIDs={trustScoreServiceOfferingIDs}
                messagingTemplateCache={messagingTemplateCache}
                messagingTemplateConfigurations={messagingTemplateConfigurations}
                availableMessagingTemplateTypes={availableMessagingTemplateTypes}
                inheritedEmailTemplateFields={inheritedEmailTemplateFields}
                mergedEmailTemplateFields={mergedEmailTemplateFields}
                selectedMessagingTemplates={selectedMessagingTemplates}
                axnAvailableAssertionStatuses={axnAvailableAssertionStatuses}
                hasUnsubmittedChangeRequest={hasUnsubmittedChangeRequest}
                axnAttributeTypeFieldCustomizations={axnAttributeTypeFieldCustomizations}
                setMessagingTemplateCache={setMessagingTemplateCache}
                setHasFetchedMessagingTemplates={handleSetHasFetchedMessagingTemplates}
            />
        </div>
    )
};